import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, Edit, Delete, Search as SearchIcon } from '@mui/icons-material';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';

const LabManagement = () => {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const itemsPerPage = 10;

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Filter labs based on search query
  const filteredLabs = labs.filter((lab) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      lab.name?.toLowerCase().includes(query) ||
      lab.address?.toLowerCase().includes(query) ||
      lab.phone?.toLowerCase().includes(query) ||
      lab.email?.toLowerCase().includes(query)
    );
  });

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    fetchLabs();
  }, []);

  const fetchLabs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/labs');
      setLabs(response.data);
    } catch (error) {
      console.error('Error fetching labs:', error);
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch labs',
        confirmButtonColor: '#1976d2',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (lab = null) => {
    if (lab) {
      setSelectedLab(lab);
      setFormData({
        name: lab.name,
        description: lab.description || '',
        address: lab.address || '',
        phone: lab.phone || '',
        email: lab.email || '',
      });
    } else {
      setSelectedLab(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLab(null);
    setFormData({
      name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
    });
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!formData.name.trim()) {
      return;
    }
    if (!formData.email.trim()) {
      return;
    }
    if (!isValidEmail(formData.email)) {
      return;
    }
    try {
      if (selectedLab) {
        await api.put(`/admin/labs/${selectedLab._id}`, formData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Lab updated successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await api.post('/admin/labs', formData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Lab created successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      }
      handleCloseDialog();
      fetchLabs();
    } catch (err) {
      // Don't show alert for validation errors (400 status) as field-level validation handles them
      if (err.response?.status !== 400) {
        SwalWithA11yFix({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Operation failed',
          confirmButtonColor: '#1976d2',
        });
      }
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/labs/${selectedLab._id}`);
      SwalWithA11yFix({
        icon: 'success',
        title: 'Success!',
        text: 'Lab deleted successfully',
        timer: 2000,
        showConfirmButton: false,
      });
      setDeleteDialogOpen(false);
      setSelectedLab(null);
      fetchLabs();
    } catch (err) {
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Delete failed',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  return (
    <Box>

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            placeholder="Search labs..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
            }}
            sx={{
              width: { xs: '100%', sm: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              },
            }}
          >
            Add Lab
          </Button>
        </Box>
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLabs.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((lab) => (
              <TableRow key={lab._id}>
                <TableCell>{lab.name}</TableCell>
                <TableCell>{lab.address || '-'}</TableCell>
                <TableCell>{lab.phone || '-'}</TableCell>
                <TableCell>{lab.email || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(lab)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedLab(lab);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!loading && labs.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredLabs.length / itemsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          {selectedLab ? 'Edit Lab' : 'Create Lab'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              error={submitted && !formData.name.trim()}
              helperText={submitted && !formData.name.trim() ? 'Lab name is required' : ''}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
              error={submitted && (!formData.email.trim() || !isValidEmail(formData.email))}
              helperText={
                submitted && !formData.email.trim()
                  ? 'Email is required'
                  : submitted && !isValidEmail(formData.email)
                  ? 'Please enter a valid email address'
                  : ''
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            {selectedLab ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>Delete Lab</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedLab?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#c62828',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabManagement;
